import { createSelector } from '@ngrx/store';
import {
  AzzurroHubDataType,
  InverterV2,
  SolarmanDataType,
} from 'ngx-core-services';

import { BlockState } from '../state/block.state';
import { SystemDefinition } from '../state/system.state';
import { selectBlockState } from './block.selectors';
import { selectSystemState } from './system.selectors';

export const selectDeviceDetailsComponent = createSelector(
  selectBlockState,
  (state: BlockState) => state.blockList.DeviceDetailsComponent
);

export const selectSystemDeviceDetailsBySelectedSystem = (instanceId: string) =>
  createSelector(
    selectSystemState,
    selectDeviceDetailsComponent,
    (systemState, deviceDetailsComponent) => {
      const selectedSystem = systemState.selectedSystemUid;
      if (!selectedSystem) {
        return null;
      }

      const instanceBlock = deviceDetailsComponent[instanceId];
      if (!instanceBlock) {
        return null;
      }

      return instanceBlock[selectedSystem]?.details;
    }
  );

export const selectSystemDeviceDetailsStatusBySelectedSystem = (
  instanceId: string
) =>
  createSelector(
    selectSystemState,
    selectDeviceDetailsComponent,
    (systemState, deviceDetailsComponent) => {
      const selectedSystem = systemState.selectedSystemUid;
      if (!selectedSystem) {
        return null;
      }

      const instanceBlock = deviceDetailsComponent[instanceId];
      if (!instanceBlock) {
        return null;
      }

      return instanceBlock[selectedSystem]?.state;
    }
  );

const selectFirmwareFromSolarmanLoggers = (
  system: SystemDefinition,
  normalizedDeviceId: string
): Partial<InverterV2> | null => {
  const loggerDevice = ((system.aws?.loggers ?? []) as SolarmanDataType[])
    .flatMap((logger) => logger.devices ?? [])
    .find((device) => device.sn?.toUpperCase() === normalizedDeviceId);

  if (!loggerDevice) {
    return null;
  }

  return {
    ARM: loggerDevice.arm,
    DSPM: loggerDevice.dspm,
    DSPS: loggerDevice.dsps,
  };
};

const selectFirmwareFromAzzurroHubDevices = (
  system: SystemDefinition,
  normalizedDeviceId: string
): Partial<InverterV2> | null => {
  const azzurroHubData = system.data as Partial<AzzurroHubDataType> | undefined;
  const inverter = azzurroHubData?.inverter?.find(
    (device) => device.sn?.toUpperCase() === normalizedDeviceId
  );

  if (!inverter) {
    return null;
  }

  return {
    ARM: inverter.arm,
    DSPM: inverter.dspm,
    DSPS: inverter.dsps,
  };
};

export const selectSystemDeviceFirmwareDetailsBySelectedSystem = (
  instanceId: string,
  deviceId?: string
) =>
  createSelector(
    selectSystemState,
    selectDeviceDetailsComponent,
    (systemState, deviceDetailsComponent): Partial<InverterV2> | null => {
      const selectedSystem = systemState.selectedSystemUid;
      if (!selectedSystem) {
        return null;
      }

      const system = systemState.systemList[selectedSystem];
      if (!system) {
        return null;
      }

      const isCertifiedPlant = system.source === 'aws';
      const normalizedDeviceId = deviceId?.toUpperCase();

      if (isCertifiedPlant && normalizedDeviceId) {
        if (system.monitoringType === 'Solarman') {
          return selectFirmwareFromSolarmanLoggers(system, normalizedDeviceId);
        }

        if (system.monitoringType === 'AzzurroHub') {
          return selectFirmwareFromAzzurroHubDevices(system, normalizedDeviceId);
        }
      }

      const instanceBlock = deviceDetailsComponent[instanceId];
      if (!instanceBlock) {
        return null;
      }

      return instanceBlock[selectedSystem]?.details ?? null;
    }
  );
